import { useCallback, useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, Phone, User, MapPin, Calendar, ArrowLeft, RefreshCw, Smartphone } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const SHOPS = new Set(["humairi", "raqiya", "raqya"]);

/**
 * DiscountMiniApp — fully standalone, customer-facing loyalty card mini-app.
 *
 * Mounted at `/d/:shop`. Contains zero references to the Meyar app shell or
 * branding. A single component handles three states:
 *
 *   1. boot     — checks localStorage for a saved customer_id and validates
 *                 it against the backend. Found → renders the card directly.
 *   2. form     — registration form (name, phone, region, optional DOB,
 *                 captcha). Submits to /api/public/join/:slug.
 *   3. card     — wallet-style card view, identical UX to the legacy
 *                 /card/:id page. Polls every 10s for live point sync.
 *
 * The component preserves every existing customer untouched — it only
 * reads from the same backend that Ma'yar writes to.
 *
 * The card's data shape already mirrors Apple's PassKit JSON
 * (`response.wallet_pass`). When real .pkpass certificates are added in the
 * future, only a sign-and-serve endpoint needs to be wired — no UI changes.
 */
export default function DiscountMiniApp() {
    const { shop } = useParams();
    const slug = (shop || "").toLowerCase();

    const [phase, setPhase] = useState("boot"); // boot | form | card
    const [brand, setBrand] = useState(null);
    const [card, setCard] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // ── Registration form state ──
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [region, setRegion] = useState("");
    const [dob, setDob] = useState("");
    const [captcha, setCaptcha] = useState(null);
    const [captchaAns, setCaptchaAns] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    // Brand the tab title + home-screen shortcut name per shop. Mirrors the
    // inline script in index.html so the name stays consistent throughout the
    // session (and matches what iOS captures on "Add to Home Screen").
    useEffect(() => {
        if (!SHOPS.has(slug)) return undefined;
        const SHORTCUT = slug === "humairi" ? "بطاقة خصم الحميري" : "بطاقة خصم الراقية";
        const prev = document.title;
        document.title = SHORTCUT;
        const setMeta = (name, content) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (!tag) {
                tag = document.createElement("meta");
                tag.setAttribute("name", name);
                document.head.appendChild(tag);
            }
            tag.setAttribute("content", content);
            return tag;
        };
        const m1 = setMeta("apple-mobile-web-app-title", SHORTCUT);
        const m2 = setMeta("application-name", SHORTCUT);
        const m3 = setMeta("apple-mobile-web-app-capable", "yes");
        const m4 = setMeta("apple-mobile-web-app-status-bar-style", "black-translucent");
        return () => {
            document.title = prev;
            [m1, m2, m3, m4].forEach((t) => t?.remove());
        };
    }, [slug]);

    // Initial boot — fetch shop info + try auto-login from localStorage.
    useEffect(() => {
        if (!SHOPS.has(slug)) return;
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/public/shops/${slug}`);
                if (alive) setBrand(r.data);
            } catch {
                if (alive) setBrand(null);
            }
            // Auto-login attempt
            try {
                const savedId = localStorage.getItem(`${slug}_customer_id`);
                if (savedId) {
                    try {
                        const c = await axios.get(`${API}/public/card/${savedId}`);
                        if (alive) {
                            setCard(c.data);
                            setPhase("card");
                            return;
                        }
                    } catch {
                        localStorage.removeItem(`${slug}_customer_id`);
                    }
                }
            } catch {/* private mode */}
            // No valid saved id → show registration form
            await newCaptcha();
            if (alive) setPhase("form");
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    // Live sync — refresh the card every 10s while visible (no spinner).
    const refreshCard = useCallback(async () => {
        const cid = card?.customer_id;
        if (!cid || document.hidden) return;
        try {
            const c = await axios.get(`${API}/public/card/${cid}`);
            setCard(c.data);
        } catch (e) {
            if (e?.response?.status === 404) {
                // Card was deleted — drop the local session.
                try { localStorage.removeItem(`${slug}_customer_id`); } catch {/* */}
                setCard(null);
                setPhase("form");
                await newCaptcha();
            }
        }
    }, [card?.customer_id, slug]);

    useEffect(() => {
        if (phase !== "card") return undefined;
        const id = setInterval(refreshCard, 10000);
        const onVis = () => { if (document.visibilityState === "visible") refreshCard(); };
        document.addEventListener("visibilitychange", onVis);
        return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    }, [phase, refreshCard]);

    const newCaptcha = async () => {
        try {
            const r = await axios.post(`${API}/public/captcha`);
            setCaptcha(r.data);
            setCaptchaAns("");
        } catch {
            setCaptcha(null);
        }
    };

    const onManualRefresh = async () => {
        setRefreshing(true);
        await refreshCard();
        setRefreshing(false);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!name.trim() || !phone.trim() || !region.trim()) {
            setErr("الاسم ورقم الهاتف والولاية مطلوبة");
            return;
        }
        if (!captcha || !captchaAns.trim()) {
            setErr("الرجاء حل رمز التحقق");
            return;
        }
        setSubmitting(true);
        try {
            const r = await axios.post(`${API}/public/join/${slug}`, {
                name: name.trim(),
                phone: phone.trim(),
                region: region.trim(),
                dob: dob.trim() || undefined,
                captcha_id: captcha.captcha_id,
                captcha_answer: captchaAns.trim(),
            });
            const cid = r.data.customer_id;
            try { localStorage.setItem(`${slug}_customer_id`, cid); } catch {/* */}
            const c = await axios.get(`${API}/public/card/${cid}`);
            setCard(c.data);
            setPhase("card");
        } catch (e2) {
            const msg = e2?.response?.data?.detail || "تعذر الإرسال — حاول مجدداً";
            setErr(typeof msg === "string" ? msg : "تعذر الإرسال");
            await newCaptcha();
        } finally {
            setSubmitting(false);
        }
    };

    if (!SHOPS.has(slug)) return <Navigate to="/d/humairi" replace />;

    if (phase === "boot" || !brand) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-neutral-50" dir="rtl">
                <Loader2 className="h-8 w-8 animate-spin text-amber-700" />
            </div>
        );
    }

    if (phase === "card" && card) {
        return <CardView card={card} brand={brand} onRefresh={onManualRefresh} refreshing={refreshing} />;
    }

    // ── Registration form ──
    return (
        <div
            className="min-h-screen bg-gradient-to-br from-neutral-50 via-amber-50/30 to-neutral-100 px-4 py-6 sm:py-10"
            dir="rtl"
        >
            <div className="mx-auto max-w-md">
                <BrandHeader brand={brand} />
                <form onSubmit={onSubmit} className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="d-form">
                    <h1 className="font-heading text-xl font-bold">سجّل لتفعيل بطاقة الولاء</h1>
                    <p className="mt-1 text-xs text-neutral-500">
                        أدخل بياناتك واحصل على بطاقة رقمية — كل {brand.per_point_omr} ر.ع لكل نقطة (حد أقصى {brand.max_points} نقاط).
                    </p>
                    <div className="mt-5 space-y-4">
                        <Field icon={User} label="الاسم الكامل" value={name} onChange={setName} placeholder="مثال: محمد العبري" testid="d-input-name" />
                        <Field icon={Phone} label="رقم الهاتف" value={phone} onChange={setPhone} placeholder="+968 ..." inputMode="tel" testid="d-input-phone" />
                        <Field icon={MapPin} label="الولاية" value={region} onChange={setRegion} placeholder="مثال: مسقط، صحار، نزوى ..." testid="d-input-region" />
                        <Field icon={Calendar} label="تاريخ الميلاد (اختياري)" value={dob} onChange={setDob} type="date" testid="d-input-dob" />
                        <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-3">
                            <label className="mb-1 flex items-center gap-1.5 text-[11px] font-bold text-amber-900">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                التحقق البشري — احسب الناتج
                            </label>
                            <div className="flex items-center gap-2">
                                <span
                                    className="rounded-lg bg-white px-3 py-2 font-mono text-base font-bold text-neutral-800 ring-1 ring-amber-200"
                                    data-testid="d-captcha-question"
                                >
                                    {captcha?.question || "..."}  =
                                </span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={captchaAns}
                                    onChange={(e) => setCaptchaAns(e.target.value)}
                                    placeholder="?"
                                    className="h-10 w-20 rounded-lg border border-amber-200 bg-white px-3 text-center font-mono text-base focus:border-amber-500 focus:outline-none"
                                    data-testid="d-captcha-answer"
                                />
                                <button
                                    type="button"
                                    onClick={newCaptcha}
                                    className="ms-auto text-[11px] font-semibold text-amber-700 hover:underline"
                                    data-testid="d-captcha-refresh"
                                >
                                    تحديث
                                </button>
                            </div>
                        </div>
                        {err ? (
                            <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid="d-error">{err}</div>
                        ) : null}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-neutral-900 text-base font-bold text-white shadow-md transition hover:bg-neutral-800 disabled:opacity-60"
                            data-testid="d-submit-btn"
                        >
                            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                            تفعيل البطاقة
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function BrandHeader({ brand }) {
    return (
        <div
            className="relative mb-6 overflow-hidden rounded-3xl px-6 py-7 text-white shadow-xl"
            style={{ background: `linear-gradient(135deg, ${brand.accent}, #1c1410)` }}
            data-testid="d-brand-header"
        >
            <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/5 blur-2xl" />
            <div className="relative flex items-center gap-4">
                <img src={brand.logo} alt="logo" className="h-16 w-16 rounded-2xl object-cover ring-4 ring-white/40" />
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/70">LOYALTY CARD · بطاقة ولاء</div>
                    <div className="font-heading text-2xl font-extrabold leading-tight">{brand.name_ar}</div>
                    <div className="text-xs text-white/70">{brand.tagline_ar}</div>
                </div>
            </div>
        </div>
    );
}

function Field({ icon: Icon, label, value, onChange, placeholder, inputMode, type = "text", testid }) {
    return (
        <label className="block">
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold text-neutral-700">
                <Icon className="h-3.5 w-3.5 text-amber-700" />
                {label}
            </div>
            <input
                type={type}
                inputMode={inputMode}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm focus:border-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
        </label>
    );
}

function CardView({ card, brand, onRefresh, refreshing }) {
    const [showSavePrompt, setShowSavePrompt] = useState(false);
    const points = card.points || 0;
    const max = card.max_points || 4;
    const per = card.per_point_omr || 5;
    const totalDiscount = card.total_discount_omr ?? points * per;
    const remaining = max - points;
    const accent = card.accent || brand.accent || "#8B5A2B";
    const completedCards = card.completed_cards || 0;
    const progressMessage = completedCards === 1
        ? "تم استلام كرت كامل — متبقي كرتان للحصول على ختم ذهبي"
        : completedCards === 2
        ? "تم استلام كرتين كاملين — متبقي كرت واحد للحصول على ختم ذهبي"
        : null;
    void remaining;

    return (
        <div className="min-h-screen bg-gradient-to-b from-neutral-100 to-neutral-200 px-4 py-6 sm:py-10" dir="rtl">
            <div className="mx-auto max-w-md">
                {/* ─── Wallet-style card ─── */}
                <div
                    className="relative overflow-hidden rounded-[28px] text-white shadow-2xl"
                    data-testid="d-wallet-card"
                    style={{ background: `linear-gradient(140deg, ${accent} 0%, #1c1410 70%, #000 100%)` }}
                >
                    <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-amber-300/15 blur-3xl" />
                    <div className="relative flex items-center justify-between px-5 pt-5">
                        <div className="flex items-center gap-3">
                            {card.logo ? (
                                <img src={card.logo} alt="logo" className="h-11 w-11 rounded-xl object-cover ring-2 ring-white/40" />
                            ) : null}
                            <div>
                                <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-white/65">LOYALTY</div>
                                <div className="font-heading text-lg font-extrabold leading-tight" data-testid="d-card-shop">{card.shop_name}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onRefresh}
                            className="rounded-full bg-white/10 p-2 backdrop-blur transition hover:bg-white/20"
                            title="تحديث"
                            data-testid="d-card-refresh-btn"
                        >
                            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                        </button>
                    </div>

                    <div className="relative mt-4 px-5">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-white/55">الاسم</div>
                        <div className="font-heading text-xl font-bold" data-testid="d-card-name">{card.name}</div>
                        <div className="mt-0.5 font-mono text-[11px] text-white/60">{card.phone} · {card.region}</div>
                    </div>

                    <div className="relative mt-5 grid grid-cols-4 gap-2 px-5">
                        {[...Array(max)].map((_, i) => {
                            const filled = i < points;
                            return (
                                <div
                                    key={i}
                                    data-testid={`d-card-stamp-${i}`}
                                    className={`relative aspect-square overflow-hidden rounded-full ring-2 ${
                                        filled ? "bg-white ring-white" : "bg-white/10 ring-white/25"
                                    } flex items-center justify-center`}
                                >
                                    {card.logo ? (
                                        <img
                                            src={card.logo}
                                            alt="stamp"
                                            className={`h-full w-full object-cover transition-opacity ${filled ? "opacity-100" : "opacity-25"}`}
                                        />
                                    ) : (
                                        <span className={`text-xl ${filled ? "text-amber-600" : "text-white/40"}`}>★</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="relative mt-3 px-5 text-center text-[10px] font-semibold uppercase tracking-widest text-white/55">
                        كل ختم = {per} ر.ع خصم
                    </div>

                    <div className="relative mt-4 grid grid-cols-2 gap-3 border-t border-white/10 px-5 py-4">
                        <div>
                            <div className="text-[9px] font-bold uppercase tracking-widest text-white/55">إجمالي الخصم</div>
                            <div className="mt-0.5 font-heading text-3xl font-extrabold" data-testid="d-card-total-discount">
                                {totalDiscount}<span className="ms-1 text-sm font-bold text-white/70">ر.ع</span>
                            </div>
                        </div>
                        <div className="text-left">
                            <div className="text-[9px] font-bold uppercase tracking-widest text-white/55">متبقي على البطاقة الذهبية</div>
                            <div className="mt-0.5 font-heading text-3xl font-extrabold" data-testid="d-card-remaining">
                                {(card.vip_max ?? 3) - (card.vip_points || 0)}<span className="ms-1 text-sm font-bold text-white/70">/ {card.vip_max ?? 3}</span>
                            </div>
                        </div>
                    </div>

                    <div className="relative border-t border-white/10 bg-black/30 px-5 py-3 text-center">
                        <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-white/45">MEMBERSHIP</div>
                        <div className="mt-0.5 font-mono text-base font-bold tracking-[0.4em]" data-testid="d-card-membership">#{card.membership_id}</div>
                    </div>
                </div>

                {/* ─── VIP Gold Card (below regular) ─── */}
                {progressMessage ? (
                    <div className="mt-4 rounded-2xl border border-amber-300/70 bg-gradient-to-r from-amber-100/80 to-yellow-100/80 px-4 py-3 text-center text-sm font-bold text-amber-900 shadow-sm" data-testid="d-progress-msg">
                        {progressMessage}
                    </div>
                ) : null}
                <VipCard card={card} />

                <div className="mt-5">
                    <button
                        type="button"
                        onClick={() => setShowSavePrompt(true)}
                        className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-neutral-900 text-sm font-bold text-white shadow-md hover:bg-neutral-800"
                        data-testid="d-card-save-btn"
                    >
                        <Smartphone className="h-4 w-4" />
                        احفظ كاختصار على الجوال
                    </button>
                </div>
            </div>

            {showSavePrompt ? <SaveModal onClose={() => setShowSavePrompt(false)} /> : null}
        </div>
    );
}

function SocialLinks({ social }) {
    if (!social || (!social.whatsapp && !social.instagram)) return null;
    const iconClass = "h-4 w-4 shrink-0 text-white/85 transition hover:text-white";
    return (
        <span className="ms-1 flex items-center gap-1" data-testid="d-card-social">
            {social.whatsapp ? (
                <a
                    href={social.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp"
                    className="rounded-full bg-white/10 p-1 backdrop-blur transition hover:bg-white/20"
                    data-testid="d-card-whatsapp"
                    onClick={(e) => e.stopPropagation()}
                >
                    <svg viewBox="0 0 24 24" fill="currentColor" className={iconClass} aria-hidden="true">
                        <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 0 5.37 0 12a11.94 11.94 0 0 0 1.64 6.03L0 24l6.16-1.61A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.25-6.19-3.48-8.52ZM12 21.82a9.82 9.82 0 0 1-5-1.37l-.36-.21-3.66.96.98-3.57-.23-.37A9.82 9.82 0 1 1 12 21.82Zm5.4-7.36c-.29-.14-1.72-.85-1.99-.95-.27-.1-.47-.14-.66.14-.19.29-.75.95-.92 1.14-.17.19-.34.22-.63.07-.29-.14-1.22-.45-2.32-1.42-.86-.76-1.44-1.71-1.61-2-.17-.29-.02-.44.13-.58.13-.13.29-.34.44-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.66-1.6-.91-2.19-.24-.58-.48-.5-.66-.51h-.56c-.19 0-.5.07-.76.36-.26.29-1 1-1 2.44 0 1.44 1.03 2.83 1.18 3.03.14.19 2.03 3.1 4.92 4.35.69.3 1.22.48 1.64.61.69.22 1.31.19 1.81.12.55-.08 1.72-.7 1.96-1.38.24-.68.24-1.26.17-1.38-.07-.12-.26-.19-.55-.34Z"/>
                    </svg>
                </a>
            ) : null}
            {social.instagram ? (
                <a
                    href={social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="rounded-full bg-white/10 p-1 backdrop-blur transition hover:bg-white/20"
                    data-testid="d-card-instagram"
                    onClick={(e) => e.stopPropagation()}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass} aria-hidden="true">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                    </svg>
                </a>
            ) : null}
        </span>
    );
}

function VipCard({ card }) {
    const vip = card.vip_points || 0;
    const vipMax = card.vip_max || 3;
    const vipPer = card.vip_per_stamp_omr || 20;
    const vipTotal = card.vip_total_discount_omr ?? vip * vipPer;
    return (
        <div
            className="relative mt-5 overflow-hidden rounded-[28px] text-white shadow-2xl ring-1 ring-amber-300/40"
            style={{ background: "linear-gradient(140deg, #d4af37 0%, #a67c11 45%, #4a3608 100%)" }}
            data-testid="d-vip-card"
        >
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/15 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-12 h-44 w-44 rounded-full bg-yellow-200/20 blur-3xl" />
            <div className="relative flex items-center justify-between px-5 pt-5">
                <div className="flex items-center gap-3">
                    {card.logo ? (
                        <img src={card.logo} alt="logo" className="h-11 w-11 rounded-xl object-cover ring-2 ring-white/60" />
                    ) : null}
                    <div>
                        <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-amber-50/90">GOLD CARD</div>
                        <div className="flex items-center gap-1.5">
                            <div className="font-heading text-lg font-extrabold leading-tight" data-testid="d-vip-shop">{card.shop_name}</div>
                            <span className="rounded-md bg-black/45 px-1.5 py-0.5 text-[10px] font-black tracking-widest text-amber-100" data-testid="d-vip-badge">VIP</span>
                            <SocialLinks social={card.social} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative mt-5 grid grid-cols-3 gap-2 px-5">
                {[...Array(vipMax)].map((_, i) => {
                    const filled = i < vip;
                    return (
                        <div
                            key={i}
                            data-testid={`d-vip-stamp-${i}`}
                            className={`relative aspect-square overflow-hidden rounded-full ring-2 ${
                                filled ? "bg-white ring-amber-100" : "bg-black/25 ring-white/30"
                            } flex items-center justify-center`}
                        >
                            {card.logo ? (
                                <img src={card.logo} alt="stamp" className={`h-full w-full object-cover transition-opacity ${filled ? "opacity-100" : "opacity-25"}`} />
                            ) : (
                                <span className={`text-xl ${filled ? "text-amber-800" : "text-white/40"}`}>★</span>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="relative mt-3 px-5 text-center text-[10px] font-semibold uppercase tracking-widest text-amber-50/85">
                كل ختم = {vipPer} ر.ع خصم
            </div>

            <div className="relative mt-4 grid grid-cols-2 gap-3 border-t border-white/15 px-5 py-4">
                <div>
                    <div className="text-[9px] font-bold uppercase tracking-widest text-amber-50/75">إجمالي الخصم</div>
                    <div className="mt-0.5 font-heading text-3xl font-extrabold" data-testid="d-vip-total-discount">
                        {vipTotal}<span className="ms-1 text-sm font-bold text-white/80">ر.ع</span>
                    </div>
                </div>
                <div className="text-left">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-amber-50/75">أختام</div>
                    <div className="mt-0.5 font-heading text-3xl font-extrabold" data-testid="d-vip-remaining">
                        {vip}<span className="ms-1 text-sm font-bold text-white/80">/ {vipMax}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SaveModal({ onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-4 pb-4 pt-12 sm:items-center sm:pb-12"
            onClick={onClose}
            data-testid="d-card-save-modal"
        >
            <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-neutral-200 px-5 py-4">
                    <div className="font-heading text-lg font-bold">{`إضافة "بطاقة خصم" لشاشة الجوال`}</div>
                    <div className="mt-0.5 text-xs text-neutral-500">احفظ هذه البطاقة لتفتحها بنقرة واحدة</div>
                </div>
                <div className="space-y-4 px-5 py-5 text-sm text-neutral-700">
                    <div>
                        <div className="mb-1 font-bold text-neutral-900">📱 iPhone (Safari)</div>
                        <ol className="ms-4 list-decimal space-y-0.5 text-xs text-neutral-600">
                            <li>اضغط زر <span className="font-bold">المشاركة</span> في أسفل المتصفح</li>
                            <li>اختر <span className="font-bold">{`"إضافة إلى الشاشة الرئيسية"`}</span></li>
                            <li>اضغط <span className="font-bold">إضافة</span></li>
                        </ol>
                    </div>
                    <div>
                        <div className="mb-1 font-bold text-neutral-900">🤖 Android (Chrome)</div>
                        <ol className="ms-4 list-decimal space-y-0.5 text-xs text-neutral-600">
                            <li>اضغط القائمة (⋮) في الأعلى</li>
                            <li>اختر <span className="font-bold">{`"إضافة إلى الشاشة الرئيسية"`}</span></li>
                        </ol>
                    </div>
                </div>
                <button type="button" onClick={onClose} className="w-full border-t border-neutral-200 bg-neutral-50 py-3 text-sm font-bold text-neutral-700 hover:bg-neutral-100">
                    تم
                </button>
            </div>
        </div>
    );
}
